import { useActions } from '../../../providers'
import { useCallback, useMemo, useState, useEffect, useRef, memo } from 'react'
import { LoadingSpinner } from '../../elements/LoadingSpinner'
import { UpdateIcon } from '../../icons'
import { GlobalState } from '@highlight/ipc'
import { AnimatePresence, motion, Variants, Transition } from 'framer-motion'
import { useActiveChatId, useChatMessages, useIsTurnLive, useGetRealChatId } from '../../../store'
import { useShareConversation } from '../../../hooks'
import { twMerge } from 'tailwind-merge'
import { Button, Tooltip } from '@highlight/ui'
import { ResizableDiv } from '../../elements/ResizableDiv'
import { Link2, Link2Off, ClipboardCheck } from 'lucide-react'

// Animation variants for fade + scale effect (main icon button)
const fadeScaleVariants: Variants = {
  initial: { opacity: 0, scale: 0.8 },
  animate: { opacity: 1, scale: 1 },
  exit: { opacity: 0, scale: 0.8 },
}

const fadeScaleTransition: Transition = { duration: 0.15, ease: 'easeOut' }

// Animation variants for delete button (includes width animation)
const deleteButtonVariants: Variants = {
  initial: { opacity: 0, scale: 0.8, width: 0 },
  animate: { opacity: 1, scale: 1, width: 'auto' },
  exit: { opacity: 0, scale: 0.8, width: 0 },
}

const deleteButtonTransition: Transition = { duration: 0.15 }

interface ConversationShareState {
  shareLink: string
  messageCountWhenShared: number
  sharedAt: number
}

/**
 * ChatShareButton component that handles sharing conversations and detecting when
 * conversations are continued after sharing as well s sharing messages
 *
 * Features:
 * - Creates shareable links for conversations and messages
 * - Detects when new messages are added after sharing
 * - Prompts user to create updated link when conversation is continued
 * - Visual states: default, sharing, copied, shared, and outdated
 */
export const ChatShareButton = memo(({ messageIds }: { messageIds?: string[] }) => {
  return (
    <ChatShareWrapper messageIds={messageIds} showTooltip={false}>
      {({ isDisabled, isSelectedMessages, state, onClick, onUpdateLink, onShare, onDeleteLink, tooltip }) => {
        const buttonClassName = useMemo(
          () =>
            twMerge(
              'transition-all duration-300 ease-in-out',
              isDisabled && 'pointer-events-none cursor-not-allowed opacity-30',
            ),
          [isDisabled],
        )

        const icon = useMemo(() => {
          switch (state) {
            case 'sharing':
              return (
                <Button size="sm" shape="square" variant="ghost" className={buttonClassName}>
                  <LoadingSpinner size={'16px'} />
                </Button>
              )
            case 'outdated':
              return (
                <Button
                  size="sm"
                  shape="square"
                  variant="ghost"
                  className={buttonClassName}
                  onClick={isDisabled ? undefined : onUpdateLink}
                >
                  <UpdateIcon size={16} />
                </Button>
              )
            case 'shared':
              return (
                <Button
                  size="sm"
                  shape="square"
                  variant="ghost"
                  className={buttonClassName}
                  onClick={isDisabled ? undefined : onShare}
                >
                  <Link2 size={18} />
                </Button>
              )
            case 'copied':
              return (
                <Button size="sm" shape="square" variant="ghost" className={buttonClassName}>
                  <ClipboardCheck size={18} />
                </Button>
              )
            default:
              return (
                <Button size="sm" shape="square" variant="ghost" className={buttonClassName}>
                  <Link2 size={18} />
                </Button>
              )
          }
        }, [state, isDisabled])

        const isMessageShare = messageIds && messageIds.length > 0

        return (
          <div className={twMerge('flex items-center gap-2', isMessageShare && 'gap-1.5')}>
            <AnimatePresence>
              {(state === 'shared' || state === 'outdated') && (
                <motion.div
                  variants={deleteButtonVariants}
                  initial="initial"
                  animate="animate"
                  exit="exit"
                  transition={deleteButtonTransition}
                >
                  <Tooltip>
                    <Tooltip.Trigger
                      render={
                        <Button
                          size="sm"
                          shape="square"
                          variant="ghost"
                          className={buttonClassName}
                          onClick={isDisabled ? undefined : onDeleteLink}
                        />
                      }
                    >
                      <Link2Off size={17} className="text-red-80" />
                    </Tooltip.Trigger>
                    <Tooltip.Content>Delete share link</Tooltip.Content>
                  </Tooltip>
                </motion.div>
              )}
            </AnimatePresence>
            <ResizableDiv
              containerClassName={twMerge(
                'flex flex-1 items-center',
                isDisabled ? 'cursor-not-allowed' : 'cursor-pointer',
              )}
              onClick={() => {
                if (isDisabled) {
                  return
                }
                onClick()
              }}
            >
              <AnimatePresence mode="wait">
                <motion.div
                  key={state}
                  variants={fadeScaleVariants}
                  initial="initial"
                  animate="animate"
                  exit="exit"
                  transition={fadeScaleTransition}
                >
                  <Tooltip>
                    <Tooltip.Trigger render={<div className="relative flex" />}>{icon}</Tooltip.Trigger>
                    <Tooltip.Content>{tooltip}</Tooltip.Content>
                  </Tooltip>
                </motion.div>
              </AnimatePresence>
            </ResizableDiv>
          </div>
        )
      }}
    </ChatShareWrapper>
  )
})
ChatShareButton.displayName = 'ChatShareButton'

export type ShareStates = 'default' | 'sharing' | 'copied' | 'shared' | 'outdated'
export type ChatShareWrapperProps = {
  children: (props: {
    isDisabled: boolean
    isSelectedMessages: boolean
    state: ShareStates
    onClick: () => void
    onUpdateLink: () => void
    onShare: () => void
    onDeleteLink: () => void
    ensureShareLink: () => Promise<string>
    tooltip: string
  }) => React.ReactNode
  messageIds?: string[]
  chatId?: string
  showTooltip?: boolean
  tooltipPosition?: 'top' | 'bottom' | 'left' | 'right'
  tooltipOffset?: number
  eventProps?: Record<'source' | string, any>
}

export const ChatShareWrapper = memo(
  ({
    children,
    messageIds,
    chatId,
    showTooltip = true,
    tooltipPosition,
    tooltipOffset,
    eventProps,
  }: ChatShareWrapperProps) => {
    const { copyTextToClipboard } = useActions()

    const [shareLink, setShareLink] = useState<string | null>(null)
    const activeChatId = chatId ?? useActiveChatId()
    const getRealChatId = useGetRealChatId()
    // Resolve any client-side temp id to the persisted backend id. A freshly
    // created chat is still keyed by its temp id here, so the share API, the
    // persisted share state, and later loads must all key off the same stable
    // real id — otherwise a link created under the real id is saved under the
    // temp key and looks unshared (or gets re-created) once the id resolves.
    const resolvedChatId = activeChatId ? getRealChatId(activeChatId) : activeChatId
    const { getShareLink, getShareLinkMessage, deleteShareLink } = useShareConversation()
    // Helper function to get the global state key for share state
    const isSelectedMessages = !!messageIds
    const getShareStateKey = (id: string | string[]) =>
      isSelectedMessages ? `message.shareState.${id}` : `conversation.shareState.${id}`

    // Helper function to save share state to global storage
    const saveShareState = (id: string | string[], shareState: ConversationShareState) => {
      GlobalState.set(getShareStateKey(id), shareState)
    }

    // Helper function to load share state from global storage
    const loadShareState = (id: string | string[]): ConversationShareState | null => {
      return GlobalState.get<ConversationShareState>(getShareStateKey(id)) || null
    }
    const [state, setState] = useState<ShareStates>(
      loadShareState(resolvedChatId ?? '')?.shareLink ? 'shared' : 'default',
    )
    const messages = useChatMessages(activeChatId)
    const isChatLoading = useIsTurnLive(activeChatId)
    // Track the message count at the time of sharing to detect continued conversations
    const messageCountWhenShared = useRef<number>(0)

    // Determine if we are sharing a single message or an entire conversation

    // Check if the assistant is actually still responding/processing
    const isAssistantResponding = isChatLoading

    // Load persisted share state when conversation changes
    useEffect(() => {
      // Determine the identifier we should use for saving/loading share state
      const targetId = isSelectedMessages ? messageIds : resolvedChatId

      if (!targetId) {
        setState('default')
        setShareLink(null)
        messageCountWhenShared.current = 0
        return
      }

      const persistedState = loadShareState(targetId)
      if (persistedState) {
        setShareLink(persistedState.shareLink)
        messageCountWhenShared.current = persistedState.messageCountWhenShared

        // For message level sharing we never mark as outdated
        if (!isSelectedMessages && messages.length > persistedState.messageCountWhenShared) {
          setState('outdated')
        } else {
          setState('shared')
        }
      } else {
        setState('default')
        setShareLink(null)
        messageCountWhenShared.current = 0
      }
    }, [resolvedChatId, messageIds])

    // Track when conversation is continued after sharing
    useEffect(() => {
      if (!isSelectedMessages && state === 'shared' && messages.length > messageCountWhenShared.current) {
        setState('outdated')
      }
    }, [messages.length, state, isSelectedMessages])

    const createShareLink = async (realChatId: string): Promise<string> => {
      const link = messageIds ? await getShareLinkMessage(realChatId, messageIds) : await getShareLink(realChatId)
      setShareLink(link)
      messageCountWhenShared.current = messages.length

      // Save share state to persistent storage
      saveShareState(isSelectedMessages ? messageIds! : realChatId, {
        shareLink: link,
        messageCountWhenShared: messages.length,
        sharedAt: Date.now(),
      })

      return link
    }

    const createAndCopyShareLink = async () => {
      if (!resolvedChatId) {
        return
      }

      setState('sharing')

      const link = await createShareLink(resolvedChatId)

      await copyTextToClipboard(link)
      setState('copied')
      setTimeout(() => {
        setState('shared')
      }, 2500)
    }

    // The current link without touching the clipboard, creating one when there is none or the chat
    // has moved past it. Share to Slack posts the link instead of copying it.
    const ensureShareLink = async (): Promise<string> => {
      if (!resolvedChatId) {
        throw new Error('Chat has no persisted id to share yet')
      }
      if ((state === 'shared' || state === 'copied') && shareLink) {
        return shareLink
      }

      setState('sharing')
      try {
        const link = await createShareLink(resolvedChatId)
        setState('shared')
        return link
      } catch (err) {
        setState(shareLink ? 'outdated' : 'default')
        throw err
      }
    }

    const handleShare = useCallback(async () => {
      if (state === 'sharing' || state === 'copied' || isAssistantResponding) {
        // Early return if sharing state invalid
        return
      }

      if (!activeChatId) {
        return
      }

      window.ampltd.trackEvent('Assistant Preview', {
        action: 'Share',
        type: isSelectedMessages ? 'message' : 'conversation',
        ...(eventProps ?? {}),
      })

      if ((state === 'shared' || state === 'outdated') && shareLink) {
        // Copy existing share link
        await copyTextToClipboard(shareLink)
        // Update state to copied
        setState('copied')
        setTimeout(() => {
          if (isSelectedMessages) {
            // Revert state back
            setState('shared')
          } else {
            const newState = messages.length > messageCountWhenShared.current ? 'outdated' : 'shared'
            // Revert state back
            setState(newState)
          }
        }, 2000)
      } else {
        try {
          await createAndCopyShareLink()
        } catch (err) {
          console.error('Error generating or copying share link:', err)
          setState('default')
        }
      }
    }, [
      state,
      isAssistantResponding,
      activeChatId,
      shareLink,
      isSelectedMessages,
      messages.length,
      messageCountWhenShared,
      setState,
      createAndCopyShareLink,
    ])

    const handleUpdateLink = useCallback(async () => {
      if (state === 'sharing' || state === 'copied' || isAssistantResponding) {
        return
      }

      if (!activeChatId) {
        return
      }

      window.ampltd.trackEvent('Assistant Preview', { action: 'Update Share' })

      try {
        await createAndCopyShareLink()
      } catch (err) {
        console.error('Error updating share link:', err)
        setState('outdated')
      }
    }, [state, isAssistantResponding, activeChatId, createAndCopyShareLink, setState])

    const handleDeleteLink = useCallback(async () => {
      if (state === 'sharing' || isAssistantResponding) {
        return
      }

      if (!resolvedChatId) {
        return
      }

      window.ampltd.trackEvent('Assistant Preview', {
        action: 'Delete Share',
        type: isSelectedMessages ? 'message' : 'conversation',
        ...(eventProps ?? {}),
      })

      try {
        // Pass messageIds when deleting message-specific share links
        await deleteShareLink(resolvedChatId, isSelectedMessages ? messageIds : undefined)

        // Clear local state
        setShareLink(null)
        messageCountWhenShared.current = 0
        setState('default')

        // Clear persisted state
        const targetId = isSelectedMessages ? messageIds : resolvedChatId
        if (targetId) {
          GlobalState.set(getShareStateKey(targetId), null)
        }
      } catch (err) {
        console.error('Error deleting share link:', err)
        // Keep the current state if deletion fails
      }
    }, [state, isAssistantResponding, resolvedChatId, deleteShareLink, isSelectedMessages, messageIds, eventProps])

    const tooltip = useMemo(() => {
      if (isAssistantResponding) {
        return 'Wait for response to complete'
      }

      const scopeNoun = isSelectedMessages ? 'message' : 'chat'

      switch (state) {
        case 'outdated':
          return 'Conversation updated - Create new chat link'
        case 'shared':
          return `Copy ${scopeNoun} link`
        case 'copied':
          return `${isSelectedMessages ? 'Message' : 'Chat'} link copied!`
        default:
          return `Create ${scopeNoun} link`
      }
    }, [state, isAssistantResponding, isSelectedMessages])

    const onClick = useMemo(() => {
      const handler = state === 'outdated' ? handleUpdateLink : handleShare
      return handler
    }, [state, handleUpdateLink, handleShare, isAssistantResponding])

    const childrenContent = children({
      isDisabled: isAssistantResponding || state === 'sharing',
      isSelectedMessages,
      state,
      onClick,
      onUpdateLink: handleUpdateLink,
      onShare: handleShare,
      onDeleteLink: handleDeleteLink,
      ensureShareLink,
      tooltip,
    })

    if (showTooltip) {
      return (
        <Tooltip>
          <Tooltip.Trigger render={<div className="relative flex" />}>{childrenContent}</Tooltip.Trigger>
          <Tooltip.Content
            side={tooltipPosition || (isSelectedMessages ? 'top' : 'bottom')}
            sideOffset={tooltipOffset !== undefined ? tooltipOffset : isSelectedMessages ? -4 : 0}
          >
            {tooltip}
          </Tooltip.Content>
        </Tooltip>
      )
    }

    return <>{childrenContent}</>
  },
)
ChatShareWrapper.displayName = 'ChatShareWrapper'
